// src/components/PastOrders.js

import React, { useEffect, useState } from 'react';
import {
  Container, Typography, List,
  ListItem, ListItemText, Divider,
  IconButton, Box, CircularProgress
} from '@mui/material';
import { Print } from '@mui/icons-material';
import api from '../api';

export default function PastOrders() {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const email = localStorage.getItem('userEmail') || '';
        const { data } = await api.get('/orders', { params: { userEmail: email } });
        const list = Array.isArray(data) ? data : (data?.orders || []);
        if (mounted) setOrders(list);
      } catch (err) {
        console.error(err);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  const handlePrint = o => {
    const itemsHtml = o.items.map(i => {
      const upcharge  = i.glutenFreeOption ? 1 : 0;
      const unitPrice = i.price + upcharge;
      const lineTotal = (unitPrice * i.quantity).toFixed(2);

      return `
        <li>
          ${i.name}${upcharge ? ' (Gluten-Free)' : ''} × ${i.quantity}
          — $${unitPrice.toFixed(2)} each → $${lineTotal}
          ${i.note ? `<br/><em>Note: ${i.note}</em>` : ''}
          ${upcharge ? `<br/><em>+$1.00 gluten-free upcharge per unit</em>` : ''}
        </li>`;
    }).join('');

    const html = `
      <html>
        <head>
          <title>Order ${o.id}</title>
          <style>
            body { font-family: Arial; padding: 20px; }
            ul { list-style: none; padding: 0; }
            li { margin: 8px 0; }
            em { color: #555; font-style: italic; }
          </style>
        </head>
        <body>
          <h1>Order ${o.id}</h1>
          <p>${new Date(o.createdAt).toLocaleString()} — ${o.orderType}</p>
          <ul>${itemsHtml}</ul>
          <p>Subtotal: $${o.subtotal.toFixed(2)}</p>
          <p>Tax: $${o.tax.toFixed(2)}</p>
          <p><strong>Total: $${o.total.toFixed(2)}</strong></p>
        </body>
      </html>`;

    const w = window.open('', '_blank');
    if (!w) return alert('Enable pop ups to print.');
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  if (loading) {
    return (
      <Container sx={{ py:4, textAlign:'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Past Orders</Typography>

      {orders.length === 0 ? (
        <Typography>No past orders.</Typography>
      ) : (
        <List>
          {orders.map(o => (
            <Box
              key={o.id}
              sx={{
                position: 'relative',
                mb: 2,
                p: 2,
                border: '1px solid #ddd',
                borderRadius: 1
              }}
            >
              <IconButton
                onClick={() => handlePrint(o)}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <Print />
              </IconButton>

              <Typography variant="h6">Order {o.id}</Typography>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {new Date(o.createdAt).toLocaleString()} — {o.orderType}
              </Typography>

              <List disablePadding>
                {o.items.map((i, idx) => {
                  const upcharge  = i.glutenFreeOption ? 1 : 0;
                  const unitPrice = i.price + upcharge;
                  const lineTotal = (unitPrice * i.quantity).toFixed(2);

                  return (
                    <Box key={idx} sx={{ mb: 1 }}>
                      <ListItem disableGutters>
                        <ListItemText
                          primary={`${i.name}${upcharge ? ' (Gluten-Free)' : ''} × ${i.quantity}`}
                          secondary={`$${unitPrice.toFixed(2)} each → $${lineTotal}`}
                        />
                      </ListItem>

                      {i.note && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ pl: 2 }}
                        >
                          Note: {i.note}
                        </Typography>
                      )}

                      {upcharge ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ pl: 2 }}
                        >
                          +$1.00 gluten free upcharge per unit
                        </Typography>
                      ) : null}
                    </Box>
                  );
                })}
              </List>

              <Divider sx={{ my: 2 }} />
              <Typography>Subtotal: ${o.subtotal.toFixed(2)}</Typography>
              <Typography>Tax: ${o.tax.toFixed(2)}</Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>Total: ${o.total.toFixed(2)}</Typography>
            </Box>
          ))}
        </List>
      )}
    </Container>
  );
}
